/**
 * Demo flight generator — fallback when no data provider is reachable.
 * Ported from the former server (server/api/opensky.js).
 */

/**
 * Compute a bounding box from a center point and radius in km.
 * @param {{lat: number, lon: number}} center
 * @param {number} radiusKm
 */
export function radiusToBoundingBox(center, radiusKm) {
  const latDelta = radiusKm / 111; // ~111 km per degree latitude
  const lonDelta = radiusKm / (111 * Math.cos(center.lat * Math.PI / 180));
  return {
    minLat: center.lat - latDelta,
    maxLat: center.lat + latDelta,
    minLon: center.lon - lonDelta,
    maxLon: center.lon + lonDelta
  };
}

/**
 * Generate demo flight data when APIs are unavailable.
 */
export function generateDemoFlights(boundingBox, count = 8) {
  const { minLat, maxLat, minLon, maxLon } = boundingBox;
  const centerLat = (minLat + maxLat) / 2;
  const centerLon = (minLon + maxLon) / 2;
  const latRange = maxLat - minLat;
  const lonRange = maxLon - minLon;

  const airlines = ['DLH', 'EWG', 'RYR', 'EZY', 'UAE', 'SWR', 'AUA', 'BAW'];
  // Mischung aller vier Klassen: Airliner, Privatjet, Kleinflugzeug, Heli
  const types = ['A320', 'C56X', 'B738', 'C172', 'A21N', 'EC35', 'GLF6', 'E190'];
  const phases = ['climbing', 'cruising', 'descending'];

  const flights = [];
  const now = Date.now();

  for (let i = 0; i < count; i++) {
    const angle = (i / count) * 2 * Math.PI + (now / 60000); // Rotate over time
    const distance = 0.3 + Math.random() * 0.4; // 30-70% of range

    const lat = centerLat + Math.sin(angle) * latRange * distance * 0.4;
    const lon = centerLon + Math.cos(angle) * lonRange * distance * 0.4;
    const altitude = 5000 + Math.random() * 10000; // 5000-15000m
    const heading = ((angle * 180 / Math.PI) + 90 + Math.random() * 30 - 15) % 360;
    const velocity = 150 + Math.random() * 150; // 150-300 m/s

    const airline = airlines[i % airlines.length];
    const flightNum = String(100 + Math.floor(Math.random() * 900));

    flights.push({
      id: `demo${i.toString(16).padStart(6, '0')}`,
      callsign: `${airline}${flightNum}`,
      position: { lat, lon, altitude, heading },
      velocity,
      verticalRate: (Math.random() - 0.5) * 10,
      onGround: false,
      timestamp: now,
      source: 'demo',
      registration: null,
      aircraftType: types[i % types.length],
      category: null,
      phase: phases[Math.floor(Math.random() * phases.length)],
      squawk: null,
      // Beispiel-Kennzeichnung Start/Landung Stuttgart (im Demo-Modus gibt es
      // keine echte Routen-Datenbank — so bleibt das STR-Badge sichtbar)
      strTag: i === 0 ? 'arrival' : i === 1 ? 'departure' : null
    });
  }

  return flights;
}
